import React, { useState, useEffect } from "react";
import config from "../Common/Configurations/APIConfig";
import apiService from "../Common/Configurations/APIService";
import Header from "../Common/Layout/Header";
import { useAuth } from "../Common/Configurations/AuthContext";
import { Link, useNavigate } from "react-router-dom";

const DMCList = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  const [pageLoading, setPageLoading] = useState(false);
  const [lists, setLists] = useState([]);
  const [rawResponse, setRawResponse] = useState({});
  
  const [pageSize, setPageSize] = useState(12);
  const [pageNumber, setPageNumber] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);
  
  const [search, setSearch] = useState("");
  const [selectedDestination, setSelectedDestination] = useState("ALL");

  const destinationTags = ["ALL", "KASHMIR", "MALAYSIA", "DUBAI", "THAILAND", "SINGAPORE", "BALI", "VIETNAM", "KERALA"];

  // IMPROVED: Added searchVal as a parameter to avoid async state issues
  const GetData = async (pNum = 1, destination = selectedDestination, searchVal = search) => {
    if (!user?.cid) return;
    setPageLoading(true);

    const queryParams = new URLSearchParams({
      search: searchVal || "", // Use the passed parameter instead of state directly
      destination: destination === "ALL" ? "" : destination, 
      pageNumber: pNum,
      pageSize: pageSize
    }).toString();

    try {
      const url = `${config.GETDMCS}/${user?.cid}?${queryParams}`;
      const res = await apiService.getAll(url); 

      if (res) {
        setLists(res.data || []);
        setPageNumber(res.pageNumber || 1);
        setTotalPages(res.totalPages || 0);
        setTotalRecords(res.totalRecords || 0);
        setRawResponse(res);
      }
    } catch (error) {
      console.error("Failed to fetch DMC list:", error);
    } finally {
      setPageLoading(false);
    }
  };

  const handleSearch = () => {
    setPageNumber(1);
    GetData(1, selectedDestination, search);
  };

  // FIXED: Explicitly passing empty search and "ALL" to GetData
  const handleReset = () => {
    setSearch("");
    setSelectedDestination("ALL");
    setPageNumber(1);
    // Passing the reset values directly to the function call
    GetData(1, "ALL", "");
  };

  const handleTagClick = (tag) => {
    setSelectedDestination(tag);
    setPageNumber(1);
    GetData(1, tag, search); // Keep the current search text when switching tags
  };

  const GetPageData = (number) => {
    setPageNumber(number);
    GetData(number, selectedDestination, search);
  };

  const GetFirstPageData = () => { GetData(1, selectedDestination, search); };
  const GetLastPageData = () => { GetData(totalPages, selectedDestination, search); };

  const pageNumbers = [];
  for (let i = 1; i <= totalPages; i++) { pageNumbers.push(i); }

  const renderPageNumbers = pageNumbers.map((number) => (
    <li className={`page-item ${pageNumber === number ? "active" : ""}`} key={number} onClick={() => GetPageData(number)}>
      <Link className="page-link">{number}</Link>
    </li>
  ));

 const handleNavigatePackages = (dmcId) => {
  navigate(`/dmcpackagesdeals?dmcId=${dmcId}`);
};
  useEffect(() => {
    if (user && !loading) { GetData(pageNumber); }
  }, [user, loading]);

  if (loading) return <p>Loading...</p>;
  if (!user) { navigate("/"); return null; }

  return (
    <div id="layout-wrapper">
      <Header User={user} />
      <div className="main-content">
        <div className="page-content">
          <div className="container-fluid">
            
            <div className="row mb-4">
              <div className="col-12">
                <div className="p-4 rounded-3 shadow-sm bg-white border">
                    <div className="row g-3 align-items-center mb-3">
                        <div className="col-md-6">
                            <div className="search-box">
                                <input 
                                    type="text" 
                                    className="form-control" 
                                    placeholder="Search by DMC Name, Brand or Destination..." 
                                    value={search} 
                                    onChange={(e) => setSearch(e.target.value)} 
                                    onKeyPress={(e) => e.key === 'Enter' && handleSearch()} 
                                />
                            </div>
                        </div>
                        <div className="col-md-6 d-flex gap-2 justify-content-md-start mt-md-0 mt-2 pt-3">
                            <button className="btn btn-primary px-4" onClick={handleSearch}>
                                <i className="ri-search-line me-1"></i> Search
                            </button>
                            <button className="btn btn-danger px-4" onClick={handleReset}>
                                <i className="ri-refresh-line me-1"></i> Reset
                            </button>
                        </div>
                    </div>

                    <div className="d-flex flex-wrap gap-2 mt-3 pt-3 border-top border-light">
                        <span className="text-muted fw-medium me-2 py-1">Filter by Destination:</span>
                        {destinationTags.map((tag) => (
                            <button
                                key={tag}
                                className={`btn btn-sm rounded-pill px-3 fw-medium ${selectedDestination === tag ? "btn-primary shadow-sm" : "btn-soft-secondary text-dark"}`}
                                onClick={() => handleTagClick(tag)}
                            >
                                {tag}
                            </button>
                        ))}
                    </div>
                </div>
              </div>
            </div>

            <div className="row">
              {pageLoading ? (
                <div className="col-12 text-center py-5"><div className="spinner-border text-primary" role="status"></div></div>
              ) : lists.length > 0 ? (
                lists.map((dmc) => (
                  <div className="col-xl-3 col-lg-4 col-md-6 mb-4" key={dmc.companyId}>
                    <div className="card h-100 border-0 shadow-sm card-animate overflow-hidden rounded-4 text-center">
                      <div className="bg-primary bg-gradient p-4" style={{ height: "80px" }}></div>
                      <div className="card-body pt-0">
                        <div className="mb-3" style={{ marginTop: "-45px" }}>
                            <div className="avatar-lg mx-auto bg-white p-2 rounded-circle shadow-sm border border-2 border-white d-flex align-items-center justify-content-center" style={{ width: '90px', height: '90px' }}>
                                <img src={dmc.logoUrl || "/assets/images/default-company.png"} alt="Logo" className="img-fluid" style={{ maxHeight: '100%', maxWidth: '100%', objectFit: 'contain' }} />
                            </div>
                        </div>
                        <h5 className="fs-16 fw-bold mb-1 text-uppercase text-truncate">{dmc.brandName || dmc.companyName}</h5>
                        <p className="text-primary fw-bold mb-3 small">
                            <span className="badge bg-soft-info text-info rounded-pill px-3 py-1">
                                <i className="ri-map-pin-user-fill me-1"></i>
                                {dmc.destinations || "Global Partner"}
                            </span>
                        </p>
                        <div className="d-flex flex-column gap-2 p-2 bg-light rounded-3 mb-3 text-start small border border-light">
                            <div className="text-truncate px-2 text-muted"><i className="ri-mail-fill text-primary me-2"></i>{dmc.email}</div>
                            <div className="px-2 text-muted"><i className="ri-phone-fill text-success me-2"></i>{dmc.phoneNumber}</div>
                        </div>
                        <div className="border-top border-light pt-3">
                            <div className="d-flex justify-content-around">
                                <div className="text-center cursor-pointer" onClick={() => handleNavigatePackages(dmc.companyId)}>
                                    <div className="avatar-xs mx-auto mb-1 bg-soft-primary text-primary rounded-circle d-flex align-items-center justify-content-center shadow-sm">
                                        <i className="ri-briefcase-4-line"></i>
                                    </div>
                                    <span className="fs-10 fw-medium">Packages</span>
                                </div>
                                <div className="text-center opacity-50"><div className="avatar-xs mx-auto mb-1 bg-soft-success text-success rounded-circle d-flex align-items-center justify-content-center"><i className="ri-car-line"></i></div><span className="fs-10">Transfers</span></div>
                                <div className="text-center opacity-50"><div className="avatar-xs mx-auto mb-1 bg-soft-warning text-warning rounded-circle d-flex align-items-center justify-content-center"><i className="ri-hotel-line"></i></div><span className="fs-10">Hotels</span></div>
                            </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="col-12 text-center py-5">
                   <div className="p-4 bg-white rounded-3 shadow-sm border border-light">
                      <i className="ri-search-eye-line display-4 text-muted opacity-25"></i>
                      <h5 className="mt-3">No DMCs found.</h5>
                   </div>
                </div>
              )}
            </div>

            {lists.length > 0 && (
                <div className="align-items-center mt-4 pt-2 justify-content-between d-flex">
                    <div className="flex-shrink-0">
                        <div className="text-muted">
                            Showing <span className="fw-semibold">{lists.length}</span> of <span className="fw-semibold">{totalRecords}</span> Results
                        </div>
                    </div>
                    <ul className="pagination pagination-separated pagination-sm mb-0">
                        <li className={`page-item ${rawResponse.previousPage === null ? "disabled" : ""}`} onClick={() => GetFirstPageData()}>
                            <Link className="page-link">First</Link>
                        </li>
                        {renderPageNumbers}
                        <li className={`page-item ${rawResponse.nextPage === null ? "disabled" : ""}`} onClick={() => GetLastPageData()}>
                            <Link className="page-link">Last</Link>
                        </li>
                    </ul>
                </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DMCList;